import type {
  Accounts,
  AccountId,
  AccountRange,
  Group,
  GroupHierarchy,
  GroupId,
  Groups,
  GroupCollapse,
  GroupMappedAccounts,
  LocalisedNames,
  MappedAccountsPerRange,
  MappingProposal,
  RawAccount,
  RawGroup,
  RawGroupLeaf,
  ReservedPostfixes,
  CapitalisedLocale,
} from '../../shared-types'
import { isRawGroup } from '../../shared-types'
import type { MappingSource } from '../../constants'
import { pickGroupLeafNameByFirstAvailableLocale } from '@app/application/account-mapping/utils'
import { decodeHtmlChars } from '@app/utils/string-helpers'
import {
  calculateReservedPostfixes,
  mappedNumberResolver,
} from './mapped-number-resolution'
import { getAccountRangeAndPostfix } from './account-numbers'

const defaultAccountGroupState = {
  groups: {},
  groupCollapse: {},
  groupHierarchy: {},
  groupList: [],
  groupMappedAccounts: {},
}

type EitherGroupOrGroupLeaf = RawGroup | RawGroupLeaf

const keepRelevantGroupData = (
  item: EitherGroupOrGroupLeaf,
  supportedLocales: CapitalisedLocale[]
): Group => ({
  accountRange: isRawGroup(item)
    ? ''
    : (item.number || '').replace(/[^a-z0-9._]/gi, ''),
  type: isRawGroup(item) ? 'group' : item.accountType ? item.accountType : '',
  ...supportedLocales.reduce((acc, locale) => {
    const key = `name${locale}`

    return {
      ...acc,
      [key]: decodeHtmlChars(
        isRawGroup(item)
          ? item.name
          : pickGroupLeafNameByFirstAvailableLocale(item, [
              locale,
              ...supportedLocales,
            ]) // prioritise each locale, but fallback to the others if missing
      ),
    }
  }, {} as Record<keyof LocalisedNames, string>),
})

type FormatAccountListDataResult = {
  accounts: Accounts
  accountIds: Array<AccountId>
  mappedAccountsPerRange: MappedAccountsPerRange
}

type RawAccountWithoutOriginalNumber = Omit<RawAccount, 'originalNumber'>

const formatAccountListData = (
  rawAccounts: Record<string, RawAccount>
): FormatAccountListDataResult => {
  const { accounts, accountsArray, mappedAccountsPerRange } = Object.keys(
    rawAccounts
  ).reduce<{
    accounts: Accounts
    accountsArray: RawAccountWithoutOriginalNumber[]
    mappedAccountsPerRange: MappedAccountsPerRange
  }>(
    (acc, key) => {
      const rawAccount = rawAccounts[key]

      const { id: rawId, mapped, number, originalNumber, ...rest } = rawAccount

      const id = rawId.toString()

      const effectiveAccount = {
        ...rest,
        mapped,
        id,
        number: originalNumber,
        initialMappedNumber: number,
      }

      acc.accounts[id] = effectiveAccount

      acc.accountsArray.push(effectiveAccount)

      // It is possible for mapped ranges that contain multiple dots to exist.
      // The last dot part acts as the postfix for mapping so it is ignored for generating the mappedRange key
      const [accountRange] = getAccountRangeAndPostfix(number)

      if (mapped && accountRange) {
        acc.mappedAccountsPerRange = addAccountToRange({
          mappedAccountsPerRange: acc.mappedAccountsPerRange,
          accountRange,
          accountId: id,
          mappedNumber: number,
          isSaved: true,
        })
      }

      return acc
    },
    {
      accounts: {},
      accountsArray: [],
      mappedAccountsPerRange: {},
    }
  )

  const sortedAccountsArray = accountsArray.sort(
    (a, b) => parseInt(a.number, 10) - parseInt(b.number, 10)
  )

  return {
    accounts,
    // Sort by ascending original number
    accountIds: sortedAccountsArray.map((account) => account.id),
    mappedAccountsPerRange,
  }
}

type AddAccountToRangeOptions = {
  mappedAccountsPerRange: MappedAccountsPerRange
  accountRange: AccountRange
  accountId: AccountId
  mappedNumber: string
  isSaved: boolean
}

const addAccountToRange = ({
  mappedAccountsPerRange,
  accountRange,
  accountId,
  mappedNumber,
  isSaved,
}: AddAccountToRangeOptions): MappedAccountsPerRange => {
  const existingMapping = mappedAccountsPerRange[accountRange] || {}
  mappedAccountsPerRange[accountRange] = {
    ...existingMapping,
    [accountId]: {
      mappedNumber,
      isSaved,
    },
  }
  return mappedAccountsPerRange
}

type FormatAccountGroupOptions = {
  parentId?: AccountId
  level?: number
  mappedAccountsPerRange: MappedAccountsPerRange
  mappingProposals?: Record<AccountRange, MappingProposal>
  supportedLocales: CapitalisedLocale[]
}

type FormatAccountGroupResult = {
  groups: Groups
  groupCollapse: GroupCollapse
  groupHierarchy: GroupHierarchy
  groupList: Array<GroupId>
  groupMappedAccounts: GroupMappedAccounts
}

type FormatAccountGroupReducer = (
  _acc: FormatAccountGroupResult,
  _group: EitherGroupOrGroupLeaf
) => FormatAccountGroupResult

const formatAccountGroup =
  ({
    parentId = '-9999',
    level = 0,
    mappedAccountsPerRange,
    mappingProposals,
    supportedLocales,
  }: FormatAccountGroupOptions): FormatAccountGroupReducer =>
  (acc, group) => {
    const groupChildren = isRawGroup(group) ? group.children : []

    const groupIdString = group.id.toString()

    const childrenData = groupChildren.reduce(
      formatAccountGroup({
        parentId: groupIdString,
        level: level + 1,
        mappedAccountsPerRange,
        mappingProposals,
        supportedLocales,
      }),
      defaultAccountGroupState
    )

    const formattedGroup = keepRelevantGroupData(group, supportedLocales)

    const alreadyMappedAccounts = formattedGroup.accountRange
      ? mappedAccountsPerRange[formattedGroup.accountRange]
      : false

    return {
      groups: {
        ...acc.groups,
        [groupIdString]: formattedGroup,
        ...childrenData.groups,
      },
      groupCollapse: {
        ...acc.groupCollapse,
        ...(alreadyMappedAccounts ? { [groupIdString]: true } : {}),
        ...childrenData.groupCollapse,
      },
      groupHierarchy: {
        ...acc.groupHierarchy,
        [group.id]: {
          level,
          parent: parentId,
          children: groupChildren.map((child) => child.id.toString()),
        },
        ...childrenData.groupHierarchy,
      },
      groupList: [...acc.groupList, groupIdString, ...childrenData.groupList],
      groupMappedAccounts: {
        ...acc.groupMappedAccounts,
        ...(alreadyMappedAccounts
          ? { [groupIdString]: alreadyMappedAccounts }
          : {}),
        ...childrenData.groupMappedAccounts,
      },
    }
  }

type UseMappingProposalsOptions = {
  mappedAccountsPerRange: MappedAccountsPerRange
  mappingProposals: Record<AccountId, MappingProposal>
  reservedPostfixes: ReservedPostfixes
  shouldUsePostfixes: boolean
}

const useMappingProposals = ({
  mappedAccountsPerRange,
  mappingProposals,
  reservedPostfixes,
  shouldUsePostfixes,
}: UseMappingProposalsOptions): MappedAccountsPerRange => {
  Object.keys(mappingProposals || {}).forEach((proposalKey) => {
    const accountNumber = mappingProposals[proposalKey].number

    const [accountRange, postfix] = getAccountRangeAndPostfix(accountNumber)
    const doesProposalContainPostFix = accountRange && postfix

    const effectiveAccountRange = doesProposalContainPostFix
      ? accountRange
      : accountNumber

    const effectiveMappedNumber = doesProposalContainPostFix
      ? accountNumber
      : mappedNumberResolver({
          accountRange: accountNumber,
          mappedAccounts: mappedAccountsPerRange[accountNumber],
          reservedPostfixes: accountRange
            ? reservedPostfixes[accountRange]
            : [],
          shouldUsePostfixes,
        }).generate()

    addAccountToRange({
      mappedAccountsPerRange,
      accountRange: effectiveAccountRange,
      accountId: proposalKey,
      mappedNumber: effectiveMappedNumber,
      isSaved: false,
    })
  })

  return mappedAccountsPerRange
}

type FormatResponseOptions = {
  accounts: Record<AccountId, RawAccount>
  accountGroups: RawGroup[]
  mappingProposals: Record<AccountId, MappingProposal>
  shouldUsePostfixes: boolean
  useInternalMappingProposals: boolean
  supportedLocales: CapitalisedLocale[]
}

type FormatResponseData = {
  accounts: Accounts
  accountIds: AccountId[]
  groups: Groups
  groupCollapse: Record<string, boolean>
  groupHierarchy: GroupHierarchy
  groupList: Array<GroupId>
  groupMappedAccounts: GroupMappedAccounts
}

export default ({
  accounts: rawAccounts,
  accountGroups: rawAccountGroups,
  mappingProposals,
  shouldUsePostfixes,
  useInternalMappingProposals,
  supportedLocales,
}: FormatResponseOptions): FormatResponseData => {
  const { accounts, accountIds, mappedAccountsPerRange } = {
    ...formatAccountListData(rawAccounts),
  }
  const topLevelChildren = rawAccountGroups

  const reservedPostfixes = calculateReservedPostfixes(accounts)

  // Store internal/external proposals on account objects
  if (useInternalMappingProposals && mappingProposals) {
    Object.keys(mappingProposals).forEach((accountId) => {
      if (accounts[accountId]) {
        const proposal = mappingProposals[accountId]
        const accountNumber = proposal.number
        const [accountRange, postfix] = getAccountRangeAndPostfix(accountNumber)
        const doesProposalContainPostFix = accountRange && postfix

        const effectiveAccountRange = doesProposalContainPostFix
          ? accountRange
          : accountNumber

        accounts[accountId].internalOrExternalProposal = {
          accountRange: effectiveAccountRange,
          source: (proposal.source || 'manual') as MappingSource,
        }
      }
    })
  }

  const effectiveMappedAccountsPerRange = useInternalMappingProposals
    ? useMappingProposals({
        mappedAccountsPerRange,
        mappingProposals,
        reservedPostfixes,
        shouldUsePostfixes,
      })
    : mappedAccountsPerRange

  const {
    groups,
    groupCollapse,
    groupHierarchy,
    groupList,
    groupMappedAccounts,
  } = topLevelChildren.reduce(
    formatAccountGroup({
      mappedAccountsPerRange: effectiveMappedAccountsPerRange,
      supportedLocales,
    }),
    defaultAccountGroupState
  )

  return {
    accounts,
    accountIds,
    groups,
    groupCollapse,
    groupHierarchy,
    groupList,
    groupMappedAccounts,
  }
}
